// BrandBook.jsx - GOD MODE v3.0 - Maximum Functionality
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './BrandBook.css';

// Organized color palettes
const COLORS = {
    purples: ['#6366f1', '#8b5cf6', '#a855f7', '#c084fc', '#7c3aed', '#6d28d9', '#5b21b6', '#4c1d95'],
    blues: ['#3b82f6', '#60a5fa', '#0ea5e9', '#38bdf8', '#06b6d4', '#2563eb', '#1d4ed8', '#1e40af'],
    greens: ['#22c55e', '#4ade80', '#10b981', '#34d399', '#14b8a6', '#16a34a', '#15803d', '#047857'],
    reds: ['#ef4444', '#f87171', '#dc2626', '#b91c1c', '#f43f5e', '#e11d48', '#be123c', '#9f1239'],
    oranges: ['#f97316', '#fb923c', '#ea580c', '#f59e0b', '#fbbf24', '#d97706', '#b45309', '#92400e'],
    pinks: ['#ec4899', '#f472b6', '#db2777', '#d946ef', '#e879f9', '#c026d3', '#a21caf', '#86198f'],
    grays: ['#6b7280', '#9ca3af', '#4b5563', '#374151', '#1f2937', '#111827', '#64748b', '#94a3b8'],
    darks: ['#0f0f23', '#1a1a2e', '#16213e', '#0f172a', '#1e1b4b', '#172554', '#0c4a6e', '#134e4a']
};

// Font list
const FONTS = ['Inter', 'Roboto', 'Poppins', 'Montserrat', 'Open Sans', 'Lato', 'Nunito', 'Raleway', 'Work Sans', 'Outfit', 'DM Sans', 'Manrope', 'Sora', 'Space Grotesk', 'Plus Jakarta Sans'];

// Load Google Font
const loadFont = (name) => {
    const id = `gfont-${name.replace(/\s/g, '')}`;
    if (!document.getElementById(id)) {
        const link = document.createElement('link');
        link.id = id;
        link.rel = 'stylesheet';
        link.href = `https://fonts.googleapis.com/css2?family=${name.replace(/\s/g, '+')}:wght@300;400;500;600;700;800&display=swap`;
        document.head.appendChild(link);
    }
};

const BrandBook = () => {
    // State
    const [config, setConfig] = useState(() => {
        try {
            const saved = localStorage.getItem('brandbook-v3');
            if (saved) {
                return JSON.parse(saved);
            }
        } catch (e) {
            console.log('No saved config');
        }
        // Default config
        return {
            primary: '#6366f1',
            secondary: '#8b5cf6',
            accent: '#fbbf24',
            success: '#22c55e',
            warning: '#f59e0b',
            error: '#ef4444',
            bgMain: '#0f0f23',
            bgCard: '#1a1a2e',
            textMain: '#ffffff',
            textMuted: '#94a3b8',
            fontHeading: 'Inter',
            fontBody: 'Roboto',
            weightHeading: '700',
            weightBody: '400',
            fontSize: 16,
            radius: 12,
            radiusBtn: 8,
            radiusCard: 16,
            shadow: 'soft',
            glass: true,
            blur: 12,
            gradientAngle: 135,
            darkMode: true
        };
    });

    const [tab, setTab] = useState('colors');
    const [colorPicker, setColorPicker] = useState({ open: false, target: 'primary' });
    const [uploadedImage, setUploadedImage] = useState(null);
    const [copied, setCopied] = useState(false);
    const fileInputRef = useRef(null);
    const canvasRef = useRef(null);

    // Load fonts
    useEffect(() => {
        loadFont(config.fontHeading);
        loadFont(config.fontBody);
    }, [config.fontHeading, config.fontBody]);

    // Save config
    useEffect(() => {
        localStorage.setItem('brandbook-v3', JSON.stringify(config));
    }, [config]);

    // Update helper
    const set = (key, val) => setConfig(prev => ({ ...prev, [key]: val }));

    // Handle image upload
    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (ev) => {
                setUploadedImage(ev.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Apply color overlay to image
    const applyStylesToImage = () => {
        if (!uploadedImage || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;

            // Draw original image
            ctx.drawImage(img, 0, 0);

            // Apply color overlay with blend mode
            ctx.globalCompositeOperation = 'overlay';
            ctx.fillStyle = config.primary + '40';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Reset blend mode
            ctx.globalCompositeOperation = 'source-over';
        };
        img.src = uploadedImage;
    };

    // Generate CSS
    const generateCSS = () => {
        const css = `/* FIXARR Brand Book CSS Variables */
:root {
  --primary: ${config.primary};
  --secondary: ${config.secondary};
  --accent: ${config.accent};
  --success: ${config.success};
  --warning: ${config.warning};
  --error: ${config.error};
  --bg-main: ${config.bgMain};
  --bg-card: ${config.bgCard};
  --text-main: ${config.textMain};
  --text-muted: ${config.textMuted};
  --font-heading: '${config.fontHeading}', sans-serif;
  --font-body: '${config.fontBody}', sans-serif;
  --weight-heading: ${config.weightHeading};
  --weight-body: ${config.weightBody};
  --font-size: ${config.fontSize}px;
  --radius: ${config.radius}px;
  --radius-btn: ${config.radiusBtn}px;
  --radius-card: ${config.radiusCard}px;
  --shadow: ${config.shadow === 'none' ? 'none' : config.shadow === 'soft' ? '0 4px 20px rgba(0,0,0,0.15)' : '0 10px 40px rgba(0,0,0,0.3)'};
  --blur: ${config.glass ? `blur(${config.blur}px)` : 'none'};
  --gradient: linear-gradient(${config.gradientAngle}deg, ${config.primary}, ${config.secondary});
}`;
        navigator.clipboard.writeText(css);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    // Color names for display
    const colorLabels = {
        primary: 'Primario',
        secondary: 'Secundario',
        accent: 'Acento',
        success: 'Éxito',
        warning: 'Advertencia',
        error: 'Error',
        bgMain: 'Fondo Principal',
        bgCard: 'Fondo Tarjeta',
        textMain: 'Texto Principal',
        textMuted: 'Texto Secundario'
    };

    return (
        <div className="bb" style={{
            '--bb-primary': config.primary,
            '--bb-secondary': config.secondary,
            '--bb-accent': config.accent,
            '--bb-gradient': `linear-gradient(${config.gradientAngle}deg, ${config.primary}, ${config.secondary})`
        }}>
            {/* Background */}
            <div className="bb-bg">
                <div className="bb-orb bb-orb-1" style={{ background: `radial-gradient(circle, ${config.primary}50, transparent)` }}></div>
                <div className="bb-orb bb-orb-2" style={{ background: `radial-gradient(circle, ${config.secondary}50, transparent)` }}></div>
            </div>

            {/* Header */}
            <header className="bb-header">
                <Link to="/" className="bb-back">← Roadmap</Link>
                <div className="bb-title">
                    <h1>🎨 Brand Book Builder <span className="bb-badge">GOD MODE</span></h1>
                </div>
                <button className={`bb-export-btn ${copied ? 'copied' : ''}`} onClick={generateCSS}>
                    {copied ? '✓ Copiado' : '📋 Copiar CSS'}
                </button>
            </header>

            {/* Tabs */}
            <nav className="bb-tabs">
                {[
                    { id: 'colors', icon: '🎨', label: 'Colores' },
                    { id: 'fonts', icon: '📝', label: 'Tipografía' },
                    { id: 'shapes', icon: '📐', label: 'Formas' },
                    { id: 'effects', icon: '✨', label: 'Efectos' },
                    { id: 'preview', icon: '👁️', label: 'Vista Previa' }
                ].map(t => (
                    <button key={t.id} className={tab === t.id ? 'active' : ''} onClick={() => setTab(t.id)}>
                        <span>{t.icon}</span> {t.label}
                    </button>
                ))}
            </nav>

            {/* Content */}
            <main className="bb-main">
                {/* COLORS TAB */}
                {tab === 'colors' && (
                    <div className="bb-section">
                        <h2>🎨 Sistema de Colores</h2>
                        <p>Click en cualquier color para cambiarlo</p>

                        <div className="bb-colors-grid">
                            {Object.entries(colorLabels).map(([key, label]) => (
                                <div
                                    key={key}
                                    className="bb-color-item"
                                    onClick={() => setColorPicker({ open: true, target: key })}
                                >
                                    <div className="bb-color-swatch" style={{ background: config[key] }}></div>
                                    <div className="bb-color-info">
                                        <span className="bb-color-label">{label}</span>
                                        <span className="bb-color-hex">{config[key]}</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Gradient Control */}
                        <div className="bb-section-sub">
                            <h3>Gradiente</h3>
                            <div className="bb-gradient-preview" style={{
                                background: `linear-gradient(${config.gradientAngle}deg, ${config.primary}, ${config.secondary})`
                            }}>
                                {config.gradientAngle}°
                            </div>
                            <input
                                type="range"
                                min="0"
                                max="360"
                                value={config.gradientAngle}
                                onChange={e => set('gradientAngle', parseInt(e.target.value))}
                            />
                        </div>
                    </div>
                )}

                {/* FONTS TAB */}
                {tab === 'fonts' && (
                    <div className="bb-section">
                        <h2>📝 Tipografía</h2>

                        {/* Heading Font */}
                        <div className="bb-font-group">
                            <label>Fuente Títulos</label>
                            <select value={config.fontHeading} onChange={e => set('fontHeading', e.target.value)}>
                                {FONTS.map(f => <option key={f} value={f}>{f}</option>)}
                            </select>
                            <div className="bb-font-preview" style={{ fontFamily: `'${config.fontHeading}', sans-serif`, fontWeight: config.weightHeading }}>
                                <span className="big">Título de Ejemplo</span>
                                <span>ABCDEFGHIJKLMNOPQRSTUVWXYZ</span>
                                <span>abcdefghijklmnopqrstuvwxyz</span>
                                <span>0123456789</span>
                            </div>
                        </div>

                        {/* Body Font */}
                        <div className="bb-font-group">
                            <label>Fuente Cuerpo</label>
                            <select value={config.fontBody} onChange={e => set('fontBody', e.target.value)}>
                                {FONTS.map(f => <option key={f} value={f}>{f}</option>)}
                            </select>
                            <div className="bb-font-preview" style={{ fontFamily: `'${config.fontBody}', sans-serif`, fontWeight: config.weightBody }}>
                                <span>Este es un texto de ejemplo para ver cómo queda la tipografía en párrafos normales de contenido.</span>
                            </div>
                        </div>

                        {/* Weights */}
                        <div className="bb-font-group">
                            <label>Peso Títulos</label>
                            <div className="bb-weight-btns">
                                {['400', '500', '600', '700', '800', '900'].map(w => (
                                    <button
                                        key={w}
                                        className={config.weightHeading === w ? 'active' : ''}
                                        style={{ fontWeight: w }}
                                        onClick={() => set('weightHeading', w)}
                                    >{w}</button>
                                ))}
                            </div>
                        </div>

                        <div className="bb-font-group">
                            <label>Peso Cuerpo</label>
                            <div className="bb-weight-btns">
                                {['300', '400', '500', '600'].map(w => (
                                    <button
                                        key={w}
                                        className={config.weightBody === w ? 'active' : ''}
                                        style={{ fontWeight: w }}
                                        onClick={() => set('weightBody', w)}
                                    >{w}</button>
                                ))}
                            </div>
                        </div>

                        {/* Font Size */}
                        <div className="bb-font-group">
                            <label>Tamaño Base: <strong>{config.fontSize}px</strong></label>
                            <input
                                type="range"
                                min="12"
                                max="20"
                                value={config.fontSize}
                                onChange={e => set('fontSize', parseInt(e.target.value))}
                            />
                        </div>
                    </div>
                )}

                {/* SHAPES TAB */}
                {tab === 'shapes' && (
                    <div className="bb-section">
                        <h2>📐 Bordes y Formas</h2>

                        <div className="bb-shapes-grid">
                            <div className="bb-shape-item">
                                <div className="bb-shape-box" style={{ borderRadius: config.radius }}></div>
                                <label>General: {config.radius}px</label>
                                <input type="range" min="0" max="32" value={config.radius} onChange={e => set('radius', parseInt(e.target.value))} />
                            </div>

                            <div className="bb-shape-item">
                                <div className="bb-shape-box btn" style={{ borderRadius: config.radiusBtn }}>Botón</div>
                                <label>Botones: {config.radiusBtn}px</label>
                                <input type="range" min="0" max="24" value={config.radiusBtn} onChange={e => set('radiusBtn', parseInt(e.target.value))} />
                            </div>

                            <div className="bb-shape-item">
                                <div className="bb-shape-box card" style={{ borderRadius: config.radiusCard }}>Card</div>
                                <label>Tarjetas: {config.radiusCard}px</label>
                                <input type="range" min="0" max="32" value={config.radiusCard} onChange={e => set('radiusCard', parseInt(e.target.value))} />
                            </div>
                        </div>

                        {/* Presets */}
                        <div className="bb-section-sub">
                            <h3>Presets Rápidos</h3>
                            <div className="bb-presets">
                                <button onClick={() => { set('radius', 0); set('radiusBtn', 0); set('radiusCard', 0); }}>
                                    <div style={{ borderRadius: 0 }}></div>Angular
                                </button>
                                <button onClick={() => { set('radius', 8); set('radiusBtn', 6); set('radiusCard', 12); }}>
                                    <div style={{ borderRadius: 8 }}></div>Suave
                                </button>
                                <button onClick={() => { set('radius', 12); set('radiusBtn', 8); set('radiusCard', 16); }}>
                                    <div style={{ borderRadius: 12 }}></div>Moderno
                                </button>
                                <button onClick={() => { set('radius', 20); set('radiusBtn', 12); set('radiusCard', 24); }}>
                                    <div style={{ borderRadius: 20 }}></div>Redondeado
                                </button>
                                <button onClick={() => { set('radius', 9999); set('radiusBtn', 9999); set('radiusCard', 24); }}>
                                    <div style={{ borderRadius: 9999 }}></div>Pill
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* EFFECTS TAB */}
                {tab === 'effects' && (
                    <div className="bb-section">
                        <h2>✨ Efectos Visuales</h2>

                        {/* Mode */}
                        <div className="bb-effect-group">
                            <h3>Modo</h3>
                            <div className="bb-toggle-btns">
                                <button className={config.darkMode ? 'active' : ''} onClick={() => set('darkMode', true)}>🌙 Oscuro</button>
                                <button className={!config.darkMode ? 'active' : ''} onClick={() => set('darkMode', false)}>☀️ Claro</button>
                            </div>
                        </div>

                        {/* Shadows */}
                        <div className="bb-effect-group">
                            <h3>Sombras</h3>
                            <div className="bb-shadow-btns">
                                {[
                                    { id: 'none', label: 'Sin sombra', shadow: 'none' },
                                    { id: 'soft', label: 'Suave', shadow: '0 4px 20px rgba(0,0,0,0.15)' },
                                    { id: 'strong', label: 'Pronunciada', shadow: '0 10px 40px rgba(0,0,0,0.3)' }
                                ].map(s => (
                                    <button
                                        key={s.id}
                                        className={config.shadow === s.id ? 'active' : ''}
                                        onClick={() => set('shadow', s.id)}
                                    >
                                        <div style={{ boxShadow: s.shadow }}></div>
                                        {s.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Glass */}
                        <div className="bb-effect-group">
                            <h3>Glassmorphism</h3>
                            <button
                                className={`bb-toggle ${config.glass ? 'on' : ''}`}
                                onClick={() => set('glass', !config.glass)}
                            >
                                {config.glass ? '✓ Activado' : '✗ Desactivado'}
                            </button>
                            {config.glass && (
                                <div className="bb-slider-group">
                                    <label>Intensidad: {config.blur}px</label>
                                    <input type="range" min="4" max="24" value={config.blur} onChange={e => set('blur', parseInt(e.target.value))} />
                                </div>
                            )}
                            <div className="bb-glass-demo" style={{
                                backdropFilter: config.glass ? `blur(${config.blur}px)` : 'none',
                                WebkitBackdropFilter: config.glass ? `blur(${config.blur}px)` : 'none'
                            }}>
                                Glass Effect Demo
                            </div>
                        </div>
                    </div>
                )}

                {/* PREVIEW TAB */}
                {tab === 'preview' && (
                    <div className="bb-section">
                        <h2>👁️ Vista Previa</h2>

                        {/* Component Preview */}
                        <div className="bb-preview-box" style={{
                            background: config.bgMain,
                            fontFamily: `'${config.fontBody}', sans-serif`,
                            fontSize: config.fontSize
                        }}>
                            {/* Header */}
                            <div className="bb-mock-header" style={{
                                background: config.bgCard,
                                borderRadius: config.radiusCard,
                                backdropFilter: config.glass ? `blur(${config.blur}px)` : 'none',
                                boxShadow: config.shadow === 'none' ? 'none' : config.shadow === 'soft' ? '0 4px 20px rgba(0,0,0,0.15)' : '0 10px 40px rgba(0,0,0,0.3)'
                            }}>
                                <span style={{ fontFamily: `'${config.fontHeading}', sans-serif`, fontWeight: config.weightHeading, color: config.textMain, fontSize: '1.3rem' }}>
                                    FIXARR
                                </span>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button style={{
                                        background: `linear-gradient(${config.gradientAngle}deg, ${config.primary}, ${config.secondary})`,
                                        color: '#fff',
                                        border: 'none',
                                        padding: '10px 20px',
                                        borderRadius: config.radiusBtn,
                                        fontFamily: `'${config.fontHeading}', sans-serif`,
                                        fontWeight: config.weightHeading,
                                        cursor: 'pointer'
                                    }}>Primario</button>
                                    <button style={{
                                        background: 'transparent',
                                        color: config.primary,
                                        border: `2px solid ${config.primary}`,
                                        padding: '10px 20px',
                                        borderRadius: config.radiusBtn,
                                        fontFamily: `'${config.fontHeading}', sans-serif`,
                                        cursor: 'pointer'
                                    }}>Secundario</button>
                                </div>
                            </div>

                            {/* Stats */}
                            <div className="bb-mock-stats">
                                {[
                                    { icon: '✅', label: 'Completadas', value: '24', color: config.success },
                                    { icon: '🧪', label: 'Testing', value: '12', color: config.warning },
                                    { icon: '📊', label: 'Progreso', value: '67%', color: config.primary }
                                ].map((stat, i) => (
                                    <div key={i} style={{
                                        background: config.bgCard,
                                        borderRadius: config.radiusCard,
                                        padding: 20,
                                        textAlign: 'center',
                                        border: `1px solid ${stat.color}30`,
                                        backdropFilter: config.glass ? `blur(${config.blur}px)` : 'none',
                                        boxShadow: config.shadow !== 'none' ? `0 4px 16px ${stat.color}20` : 'none'
                                    }}>
                                        <div style={{ fontSize: '1.5rem' }}>{stat.icon}</div>
                                        <div style={{ fontFamily: `'${config.fontHeading}', sans-serif`, fontWeight: config.weightHeading, color: config.textMain, fontSize: '1.5rem' }}>{stat.value}</div>
                                        <div style={{ color: config.textMuted, fontSize: '0.85rem' }}>{stat.label}</div>
                                    </div>
                                ))}
                            </div>

                            {/* Alerts */}
                            <div className="bb-mock-alerts">
                                <div style={{ background: `${config.success}20`, borderLeft: `4px solid ${config.success}`, padding: 12, borderRadius: config.radius, color: config.textMain }}>
                                    ✓ Operación completada con éxito
                                </div>
                                <div style={{ background: `${config.warning}20`, borderLeft: `4px solid ${config.warning}`, padding: 12, borderRadius: config.radius, color: config.textMain }}>
                                    ⚠ Hay tareas pendientes de revisión
                                </div>
                                <div style={{ background: `${config.error}20`, borderLeft: `4px solid ${config.error}`, padding: 12, borderRadius: config.radius, color: config.textMain }}>
                                    ✕ Error al procesar la solicitud
                                </div>
                            </div>
                        </div>

                        {/* Image Upload & Apply Styles */}
                        <div className="bb-image-section">
                            <h3>📸 Aplicar a tu Captura</h3>
                            <p>Sube una captura de pantalla de tu dashboard y ve cómo quedarían los colores</p>

                            <input
                                type="file"
                                ref={fileInputRef}
                                accept="image/*"
                                onChange={handleImageUpload}
                                style={{ display: 'none' }}
                            />

                            <div className="bb-upload-area" onClick={() => fileInputRef.current?.click()}>
                                {uploadedImage ? (
                                    <img src={uploadedImage} alt="Uploaded" />
                                ) : (
                                    <>
                                        <span className="upload-icon">📤</span>
                                        <span>Click para subir captura</span>
                                    </>
                                )}
                            </div>

                            {uploadedImage && (
                                <div className="bb-image-actions">
                                    <button onClick={applyStylesToImage}>🎨 Aplicar Colores</button>
                                    <button onClick={() => setUploadedImage(null)}>🗑️ Eliminar</button>
                                </div>
                            )}

                            <canvas ref={canvasRef} className="bb-canvas" />
                        </div>
                    </div>
                )}
            </main>

            {/* Quick Preview Sidebar */}
            <aside className="bb-aside">
                <h4>Vista Rápida</h4>
                <div className="bb-quick-card" style={{
                    background: config.bgCard,
                    borderRadius: config.radiusCard
                }}>
                    <div style={{
                        background: `linear-gradient(${config.gradientAngle}deg, ${config.primary}, ${config.secondary})`,
                        padding: 16,
                        borderRadius: `${config.radiusCard}px ${config.radiusCard}px 0 0`
                    }}>
                        <span style={{ fontFamily: `'${config.fontHeading}', sans-serif`, fontWeight: config.weightHeading, color: '#fff' }}>FIXARR</span>
                    </div>
                    <div style={{ padding: 16, color: config.textMain }}>
                        <p style={{ fontFamily: `'${config.fontHeading}', sans-serif`, fontWeight: config.weightHeading, margin: '0 0 8px' }}>Título</p>
                        <p style={{ color: config.textMuted, fontSize: '0.85rem', margin: 0 }}>Texto secundario</p>
                        <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                            <button style={{ background: config.primary, color: '#fff', border: 'none', padding: '8px 16px', borderRadius: config.radiusBtn }}>OK</button>
                            <button style={{ background: config.accent, color: '#1e293b', border: 'none', padding: '8px 16px', borderRadius: config.radiusBtn }}>Acción</button>
                        </div>
                    </div>
                </div>
                <div className="bb-quick-colors">
                    {[config.primary, config.secondary, config.accent, config.success, config.warning, config.error].map((c, i) => (
                        <div key={i} style={{ background: c }}></div>
                    ))}
                </div>
            </aside>

            {/* Color Picker Modal */}
            {colorPicker.open && (
                <div className="bb-modal-overlay" onClick={() => setColorPicker({ ...colorPicker, open: false })}>
                    <div className="bb-modal" onClick={e => e.stopPropagation()}>
                        <h3>Seleccionar: {colorLabels[colorPicker.target]}</h3>

                        {Object.entries(COLORS).map(([category, colors]) => (
                            <div key={category} className="bb-modal-category">
                                <span className="bb-modal-cat-label">{category}</span>
                                <div className="bb-modal-colors">
                                    {colors.map(color => (
                                        <button
                                            key={color}
                                            className={config[colorPicker.target] === color ? 'selected' : ''}
                                            style={{ background: color }}
                                            onClick={() => {
                                                set(colorPicker.target, color);
                                                setColorPicker({ ...colorPicker, open: false });
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}

                        {/* Custom Color Input */}
                        <div className="bb-modal-custom">
                            <input
                                type="color"
                                value={config[colorPicker.target]}
                                onChange={e => set(colorPicker.target, e.target.value)}
                            />
                            <input
                                type="text"
                                value={config[colorPicker.target]}
                                onChange={e => set(colorPicker.target, e.target.value)}
                            />
                            <button onClick={() => setColorPicker({ ...colorPicker, open: false })}>Aplicar</button>
                        </div>

                        <button className="bb-modal-close" onClick={() => setColorPicker({ ...colorPicker, open: false })}>
                            Cerrar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BrandBook;
